import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { TUpdateOwnDriverInfoForm } from '@/modules/driver/model/driver';
import { AppDispatch } from '@/root-store';
import { useLicensingAuthorityTypes } from '@/shared/hooks/useLicensingAuthorityTypes';
import { SelectField } from '@src-common/view/adapters/reactHookForm/SelectField';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';

import { IRelatedInfoFieldsProps, IRelatedInfoHandle } from './config';
import { HomeAddressTextField } from './HomeAddressField';
import { useDriverTarget } from './useDriverTarget';
import { useRelatedInfoForm } from './useRelatedInfoForm';

type TDriverLicensingForm = Pick<
  TUpdateOwnDriverInfoForm,
  'licensingAuthority' | 'licenseNumber' | 'homeAddress'
>;

const messages = defineMessages({
  licensingAuthorityTitle: {
    defaultMessage: 'Licensing authority',
    id: '/Q8aMx',
    description: 'field title',
  },
  licensingAuthorityDesc: {
    defaultMessage: 'Learn more about license types <a>here</a>.',
    id: '1s5SCC',
    description: 'field description',
  },
  driverLicenseTitle: {
    defaultMessage: 'Private Hire Driver License Number',
    id: 'pFGlVW',
    description: 'PHD License Number field title',
  },
  driverLicenseDesc: {
    defaultMessage: 'Full PHD license number from your licensing authority.',
    id: 'NB/idy',
    description: 'input tip',
  },
});

const DriverLicensingFields = React.forwardRef<IRelatedInfoHandle, IRelatedInfoFieldsProps>(
  ({ scope }, ref) => {
    const intl = useIntl();
    const dispatch = useDispatch() as AppDispatch;
    const licensingAuthorityTypes = useLicensingAuthorityTypes();
    const links = useExternalLinks();

    const { driver, updateDriver } = useDriverTarget(scope);

    const onSubmit = useCallback(
      (formValues: TDriverLicensingForm) => dispatch(updateDriver(formValues)).unwrap(),
      [dispatch, updateDriver],
    );

    const { control } = useRelatedInfoForm<TDriverLicensingForm>(ref, {
      defaultValues: {
        licensingAuthority: driver?.licensingAuthority ?? null,
        licenseNumber: driver?.licenseNumber ?? '',
        homeAddress: driver?.homeAddress ?? '',
      },
      onSubmit,
    });

    return (
      <>
        <SelectField
          name="licensingAuthority"
          title={intl.formatMessage(messages.licensingAuthorityTitle)}
          description={intl.formatMessage(messages.licensingAuthorityDesc, {
            a: (text: React.ReactNode) => (
              <a href={links.licensingAuthorities} target="_blank" rel="noreferrer">
                {text}
              </a>
            ),
          })}
          control={control}
          items={licensingAuthorityTypes}
          isRequired={true}
        />
        <TextField
          name="licenseNumber"
          title={intl.formatMessage(messages.driverLicenseTitle)}
          placeholder="12345678"
          description={intl.formatMessage(messages.driverLicenseDesc)}
          control={control}
          isRequired={true}
        />
        <HomeAddressTextField control={control} />
      </>
    );
  },
);

DriverLicensingFields.displayName = 'DriverLicensingFields';

export { DriverLicensingFields };
